import { useMemo, useState } from "react";
import { useAdminClients } from "@/api/useAdmin";
import { useMe } from "@/api/useMe";
import { AdminHeader } from "./AdminHeader";
import { AdminKpis } from "./AdminKpis";
import { StatusPill } from "@/components/StatusPill";
import { Dialog } from "@/components/Dialog";
import { Button } from "@/components/Button";
import { cn } from "@/lib/cn";
import {
  useCreateClient,
  useInviteUser,
  type CreateClientBody,
  type InviteUserBody,
} from "@/api/adminClientsMutations";
import type { Role } from "@cura-studio/shared";

/**
 * Clients & access — split layout: filterable client list (left) +
 * detail panel (right) with tabbed Overview / Users / Access & brand /
 * Visible kits. New-client + invite-user dialogs land in Phase 2 once
 * the API can persist; for Phase 1 the controls render but the actions
 * are no-ops with explanatory tooltips.
 */
export function ClientsPage() {
  const { data: clients = [], isLoading } = useAdminClients();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "pending" | "suspended">("all");
  const [createOpen, setCreateOpen] = useState(false);

  const filtered = useMemo(
    () =>
      clients.filter(
        (c) =>
          (statusFilter === "all" || c.status === statusFilter) &&
          (!q ||
            c.name.toLowerCase().includes(q.toLowerCase()) ||
            c.slug.includes(q.toLowerCase()) ||
            c.domain.includes(q.toLowerCase())),
      ),
    [clients, q, statusFilter],
  );

  const client = clients.find((c) => c.id === (selectedId ?? clients[0]?.id));

  return (
    <div className="mx-auto max-w-[1480px] px-8 py-8">
      <AdminHeader
        eyebrow="Studio admin · Clients"
        title="Clients & access"
        subtitle="Create client organisations, invite users, manage their seats. Brand profiles can be locked so practice managers cannot override."
        actions={
          <Button
            onClick={() => setCreateOpen(true)}
            className="bg-[var(--admin)] hover:bg-[var(--admin-dark)] active:bg-[var(--admin-dark)]"
          >
            + New client
          </Button>
        }
      />

      <CreateClientDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(id) => setSelectedId(id)}
      />

      <AdminKpis />

      <div className="mb-3.5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="search"
            placeholder="Search by name, slug or domain…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="min-w-[260px] rounded border border-[var(--border-2)] bg-white px-3 py-2 text-[13px] outline-none focus:border-fg-1 focus:ring-2 focus:ring-[var(--border-focus)]"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
            className="rounded border border-[var(--border-2)] bg-white px-3 py-2 text-[13px] outline-none focus:border-fg-1 focus:ring-2 focus:ring-[var(--border-focus)]"
          >
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="pending">Pending</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>
        <div className="text-[12px] text-fg-3">
          {filtered.length} of {clients.length} clients
        </div>
      </div>

      <div className="grid grid-cols-[320px_1fr] items-start gap-5">
        {/* Client list */}
        <aside className="overflow-hidden rounded-lg border border-[var(--border-1)] bg-white">
          <div className="border-b border-[var(--border-1)] bg-[var(--admin-wash,#F9F7FD)] px-4 py-3">
            <h3 className="m-0 text-sm font-semibold text-fg-1">All clients</h3>
          </div>
          <ul data-chrome className="m-0 list-none p-1">
            {isLoading ? (
              <li className="px-3 py-2 text-fg-3">Loading…</li>
            ) : (
              filtered.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(c.id)}
                    className={cn(
                      "flex w-full items-center gap-2.5 rounded px-3 py-2.5 text-left text-[13px]",
                      client?.id === c.id
                        ? "bg-[var(--admin-tint)] text-[var(--admin-dark)]"
                        : "text-fg-1 hover:bg-bg-2",
                    )}
                  >
                    <span
                      className="h-2 w-2 flex-shrink-0 rounded-full"
                      style={{ background: c.accent }}
                      aria-hidden
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-semibold">{c.name}</span>
                      <span className="block truncate font-mono text-[11px] text-fg-3">{c.slug}</span>
                    </span>
                    <StatusPill status={c.status} />
                  </button>
                </li>
              ))
            )}
          </ul>
        </aside>

        {/* Detail */}
        {client ? (
          <ClientDetail clientId={client.id} />
        ) : (
          <div className="rounded-lg border border-[var(--border-1)] bg-white p-12 text-center text-fg-3">
            Select a client to view detail.
          </div>
        )}
      </div>
    </div>
  );
}

function ClientDetail({ clientId }: { clientId: string }) {
  const { data: clients = [] } = useAdminClients();
  const client = clients.find((c) => c.id === clientId);
  const [tab, setTab] = useState<"overview" | "users" | "access" | "kits">("overview");
  const [inviteOpen, setInviteOpen] = useState(false);

  if (!client) return null;

  return (
    <div className="overflow-hidden rounded-lg border border-[var(--border-1)] bg-white">
      <div className="flex items-center justify-between border-b border-[var(--border-1)] bg-[var(--admin-wash,#F9F7FD)] px-5 py-4">
        <div className="flex items-center gap-3.5">
          <div
            className="grid h-14 w-14 place-items-center rounded-md border border-[var(--border-1)] bg-white p-1.5 text-center text-[14px] font-extrabold leading-tight tracking-[-0.02em]"
            style={{ color: client.accent }}
          >
            {client.logoText}
          </div>
          <div>
            <h2 className="m-0 text-xl font-semibold text-fg-1">{client.name}</h2>
            <div className="font-mono text-[12px] text-fg-3">{client.slug} · {client.domain}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusPill status={client.status} />
          {client.brandLocked && <StatusPill status="locked" />}
        </div>
      </div>

      <div className="px-5 py-4">
        <div className="mb-5 flex gap-1 border-b border-[var(--border-1)]">
          {(
            [
              ["overview", "Overview"],
              ["users",    `Users (${client.users.length})`],
              ["access",   "Access & brand"],
              ["kits",     "Visible kits"],
            ] as const
          ).map(([k, label]) => (
            <button
              key={k}
              type="button"
              onClick={() => setTab(k)}
              className={cn(
                "-mb-px border-b-2 px-3.5 py-2.5 text-[13px] font-semibold transition-colors",
                tab === k
                  ? "border-[var(--admin)] text-[var(--admin)]"
                  : "border-transparent text-fg-3 hover:text-fg-1",
              )}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "overview" && (
          <div className="grid grid-cols-2 gap-6">
            <div>
              <Eyebrow>Primary contact</Eyebrow>
              <div className="text-[14px] font-semibold">{client.primaryContact}</div>
              <div className="text-[13px] text-fg-3">{client.primaryEmail}</div>

              <Eyebrow className="mt-4">Plan &amp; account</Eyebrow>
              <div className="text-[13px]">
                {client.plan} · <span className="font-mono">{client.account}</span>
              </div>
              <div className="text-[12px] text-fg-3">Contract start {client.contractStart}</div>
            </div>
            <div>
              <Eyebrow>Activity</Eyebrow>
              <div className="grid grid-cols-2 gap-3">
                <Stat label="Seats" value={client.seats} />
                <Stat label="Active orders" value={client.activeOrders} />
                <Stat label="Total spend" value={`£${client.spend.toLocaleString("en-GB", { maximumFractionDigits: 2 })}`} />
                <Stat label="Updated" value={client.updated} small />
              </div>
            </div>
          </div>
        )}

        {tab === "users" && (
          <>
            <div className="mb-3 flex items-center justify-between">
              <p className="m-0 text-[13px] text-fg-3">
                {client.users.length} user{client.users.length === 1 ? "" : "s"} on this tenant
              </p>
              <Button
                size="sm"
                onClick={() => setInviteOpen(true)}
                className="bg-[var(--admin)] hover:bg-[var(--admin-dark)] active:bg-[var(--admin-dark)]"
              >
                + Invite user
              </Button>
            </div>
            <InviteUserDialog
              open={inviteOpen}
              onOpenChange={setInviteOpen}
              tenantId={clientId}
              tenantName={client.name}
            />
          <div className="overflow-hidden rounded-md border border-[var(--border-1)]">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-[var(--border-1)] bg-bg-2 text-left">
                  {["Name", "Email", "Role", "Status", "Last seen"].map((h) => (
                    <th key={h} className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-fg-3">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {client.users.map((u) => (
                  <tr key={u.id} className="border-b border-[var(--border-1)] last:border-0">
                    <td className="px-3 py-2.5 text-[13px] font-semibold text-fg-1">{u.name}</td>
                    <td className="px-3 py-2.5 font-mono text-[12px] text-fg-3">{u.email}</td>
                    <td className="px-3 py-2.5 text-[13px] text-fg-2">{u.role}</td>
                    <td className="px-3 py-2.5"><StatusPill status={u.status} /></td>
                    <td className="px-3 py-2.5 text-[13px] text-fg-3">{u.lastSeen}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}

        {tab === "access" && (
          <div className="flex flex-col gap-4">
            <div className="flex gap-3 rounded-md border-l-[3px] border-[var(--brand-link)] bg-[#F3F9FC] p-4">
              <div>
                <h4 className="m-0 mb-1 text-[14px] font-semibold text-fg-1">How access works</h4>
                <p className="m-0 text-[13px] text-fg-2">
                  Invited users get an email magic link. Role controls capabilities:
                  Practice managers order, Approvers sign off proofs, Designers edit drafts.
                </p>
              </div>
            </div>
            <div className="overflow-hidden rounded-md border border-[var(--border-1)]">
              <div className="border-b border-[var(--border-1)] bg-bg-1 px-4 py-3">
                <h3 className="m-0 text-sm font-semibold text-fg-1">Brand profile</h3>
              </div>
              <div className="flex items-center justify-between p-4">
                <div>
                  <div className="font-semibold">{client.brandLocked ? "Locked by admin" : "Self-serve"}</div>
                  <div className="mt-0.5 text-[12px] text-fg-3">
                    {client.brandLocked
                      ? "Practice managers cannot change logo, palette or tagline."
                      : "Practice managers can change logo, palette and tagline at any time."}
                  </div>
                </div>
                <span className="rounded-full bg-bg-2 px-3 py-1 text-[12px] font-semibold text-fg-2">
                  {client.brandLocked ? "Locked" : "Open"}
                </span>
              </div>
            </div>
          </div>
        )}

        {tab === "kits" && <ClientVisibleKits clientId={clientId} />}
      </div>
    </div>
  );
}

function ClientVisibleKits({ clientId }: { clientId: string }) {
  const { data: kits = [] } = useAdminClientKits(clientId);
  return (
    <>
      <p className="m-0 mb-3 text-[13px] text-fg-3">
        Kits this client can see in their catalogue. Edit visibility on the <strong>Catalogue</strong> tab.
      </p>
      <div className="overflow-hidden rounded-md border border-[var(--border-1)]">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-[var(--border-1)] bg-bg-2 text-left">
              {["Kit", "Code", "TAT", "Unit price", "Tag"].map((h) => (
                <th key={h} className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-fg-3">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {kits.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-fg-3">
                  No kits visible. This client cannot place orders until at least one kit is enabled.
                </td>
              </tr>
            ) : (
              kits.map((k) => (
                <tr key={k.id} className="border-b border-[var(--border-1)] last:border-0">
                  <td className="px-3 py-2.5 text-[13px] font-semibold">{k.title}</td>
                  <td className="px-3 py-2.5 font-mono text-[12px] text-fg-3">{k.code}</td>
                  <td className="px-3 py-2.5 text-[13px] text-fg-2">{k.tat}</td>
                  <td className="px-3 py-2.5 font-mono text-[13px] tabular-nums">£{k.price.toFixed(2)}</td>
                  <td className="px-3 py-2.5 text-[12px] text-fg-3">{k.tag ?? "—"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

// Reuses the same admin catalogue source; filters to kits visible to a client.
import { useAdminCatalogue } from "@/api/useAdmin";
function useAdminClientKits(clientId: string) {
  const q = useAdminCatalogue();
  return {
    ...q,
    data: q.data?.filter((k) => k.active && k.visibleTo.includes(clientId)) ?? [],
  };
}

function Eyebrow({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn("mb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-fg-mute", className)}>
      {children}
    </div>
  );
}
function Stat({ label, value, small }: { label: string; value: string | number; small?: boolean }) {
  return (
    <div>
      <Eyebrow>{label}</Eyebrow>
      <div className={cn("font-bold text-fg-1", small ? "text-[14px]" : "text-[18px]")}>{value}</div>
    </div>
  );
}

/* ── New-client dialog ──────────────────────────────────────────────── */

// We pull ApiError lazily down here to avoid leaking it into the
// top-of-file imports the visual scan depends on.
import { ApiError } from "@/api/client";

const SLUG_RE = /^[a-z0-9][a-z0-9-]*$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type CreateClientDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (tenantId: string) => void;
};

function CreateClientDialog({ open, onOpenChange, onCreated }: CreateClientDialogProps) {
  const [form, setForm] = useState<CreateClientBody>({
    name: "",
    slug: "",
    domain: "",
    plan: "NHS PSP",
    primaryEmail: "",
    primaryContact: "",
  });
  const [serverError, setServerError] = useState<string | null>(null);
  const mutation = useCreateClient();

  const errs = validateCreateClient(form);
  const canSubmit = Object.keys(errs).length === 0 && !mutation.isPending;

  function reset() {
    setForm({ name: "", slug: "", domain: "", plan: "NHS PSP", primaryEmail: "", primaryContact: "" });
    setServerError(null);
    mutation.reset();
  }

  async function onSubmit() {
    setServerError(null);
    try {
      // Strip empty domain — the server schema treats domain as optional but
      // enforces min(3) when present; an empty string would 400.
      const body: CreateClientBody = {
        ...form,
        domain: form.domain && form.domain.trim().length >= 3 ? form.domain.trim() : undefined,
      };
      const res = await mutation.mutateAsync(body);
      onCreated(res.client.id);
      reset();
      onOpenChange(false);
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string; field?: string } | null;
        setServerError(body?.error ?? `Could not create client (HTTP ${err.status})`);
      } else {
        setServerError("Could not create client.");
      }
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) reset();
      }}
      title="Create new client"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            loading={mutation.isPending}
            disabled={!canSubmit}
            className="bg-[var(--admin)] hover:bg-[var(--admin-dark)] active:bg-[var(--admin-dark)]"
          >
            {mutation.isPending ? "Creating…" : "Create client"}
          </Button>
        </>
      }
    >
      <div className="grid gap-3">
        <Field label="Client name" error={errs.name}>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Northshore Health Hub"
            className="w-full rounded border border-[var(--border-2)] bg-white px-3 py-2 text-[13px] outline-none focus:border-fg-1 focus:ring-2 focus:ring-[var(--border-focus)]"
          />
        </Field>
        <Field
          label="Slug"
          hint="Lowercase, digits and dashes only. Used in URLs and the audit log."
          error={errs.slug}
        >
          <input
            type="text"
            value={form.slug}
            onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase() })}
            placeholder="northshore"
            className="w-full rounded border border-[var(--border-2)] bg-white px-3 py-2 font-mono text-[13px] outline-none focus:border-fg-1 focus:ring-2 focus:ring-[var(--border-focus)]"
          />
        </Field>
        <Field label="Domain" hint="Optional. Email domain we'll match new signups against." error={errs.domain}>
          <input
            type="text"
            value={form.domain ?? ""}
            onChange={(e) => setForm({ ...form, domain: e.target.value })}
            placeholder="northshore.nhs.uk"
            className="w-full rounded border border-[var(--border-2)] bg-white px-3 py-2 text-[13px] outline-none focus:border-fg-1 focus:ring-2 focus:ring-[var(--border-focus)]"
          />
        </Field>
        <Field label="Plan">
          <select
            value={form.plan ?? "NHS PSP"}
            onChange={(e) => setForm({ ...form, plan: e.target.value })}
            className="w-full rounded border border-[var(--border-2)] bg-white px-3 py-2 text-[13px] outline-none focus:border-fg-1 focus:ring-2 focus:ring-[var(--border-focus)]"
          >
            <option value="NHS PSP">NHS PSP</option>
            <option value="Private">Private</option>
            <option value="Pilot">Pilot</option>
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Primary contact" error={errs.primaryContact}>
            <input
              type="text"
              value={form.primaryContact}
              onChange={(e) => setForm({ ...form, primaryContact: e.target.value })}
              placeholder="Dr Anna Lee"
              className="w-full rounded border border-[var(--border-2)] bg-white px-3 py-2 text-[13px] outline-none focus:border-fg-1 focus:ring-2 focus:ring-[var(--border-focus)]"
            />
          </Field>
          <Field label="Primary email" error={errs.primaryEmail}>
            <input
              type="email"
              value={form.primaryEmail}
              onChange={(e) => setForm({ ...form, primaryEmail: e.target.value })}
              placeholder="anna.lee@northshore.nhs.uk"
              className="w-full rounded border border-[var(--border-2)] bg-white px-3 py-2 text-[13px] outline-none focus:border-fg-1 focus:ring-2 focus:ring-[var(--border-focus)]"
            />
          </Field>
        </div>
        <p className="m-0 mt-1 text-[12px] text-fg-3">
          The primary contact lands as <strong>Practice manager</strong> in INVITED status —
          they'll activate on first sign-in. Email delivery wires in Phase 4.5.
        </p>
        {serverError && (
          <div className="rounded-md border border-[#E2A0A0] bg-[#FBE9E9] px-3 py-2 text-[13px] text-[#8C2020]">
            {serverError}
          </div>
        )}
      </div>
    </Dialog>
  );
}

function validateCreateClient(f: CreateClientBody): Partial<Record<keyof CreateClientBody, string>> {
  const errs: Partial<Record<keyof CreateClientBody, string>> = {};
  if (f.name.trim().length < 2) errs.name = "Name is required (2+ chars).";
  if (!SLUG_RE.test(f.slug)) errs.slug = "Lowercase letters, digits and dashes only.";
  else if (f.slug.length < 2 || f.slug.length > 40) errs.slug = "2–40 characters.";
  if (f.domain && f.domain.trim().length > 0 && f.domain.trim().length < 3) {
    errs.domain = "Domain too short.";
  }
  if (!EMAIL_RE.test(f.primaryEmail)) errs.primaryEmail = "Valid email required.";
  if (f.primaryContact.trim().length < 2) errs.primaryContact = "Contact name required.";
  return errs;
}

/* ── Invite-user dialog ─────────────────────────────────────────────── */

type InviteUserDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenantId: string;
  tenantName: string;
};

// Base roles every admin caller can assign. STUDIO_ADMIN is appended at
// runtime when the caller is a STUDIO_ADMIN — CLIENT_ADMINs (should the
// admin Clients surface ever be reused inside a CLIENT_ADMIN session) must
// not be able to escalate anyone to the studio tier.
const BASE_ROLES: { value: Role; label: string }[] = [
  { value: "CLIENT_ADMIN", label: "Client admin" },
  { value: "PRACTICE_MANAGER", label: "Practice manager" },
  { value: "APPROVER", label: "Approver" },
  { value: "DESIGNER", label: "Designer" },
  { value: "VIEWER", label: "Viewer" },
];

function InviteUserDialog({ open, onOpenChange, tenantId, tenantName }: InviteUserDialogProps) {
  const { data: me } = useMe();
  const isStudioAdmin = me?.user?.role === "STUDIO_ADMIN";
  const roles = isStudioAdmin
    ? [{ value: "STUDIO_ADMIN" as Role, label: "Studio admin" }, ...BASE_ROLES]
    : BASE_ROLES;

  const [form, setForm] = useState<InviteUserBody>({ email: "", name: "", role: "PRACTICE_MANAGER" });
  const [serverError, setServerError] = useState<string | null>(null);
  const [magicLink, setMagicLink] = useState<string | null>(null);
  const mutation = useInviteUser(tenantId);

  const errs: Partial<Record<keyof InviteUserBody, string>> = {};
  if (!EMAIL_RE.test(form.email)) errs.email = "Valid email required.";
  if (form.name.trim().length < 2) errs.name = "Name required.";
  const canSubmit = Object.keys(errs).length === 0 && !mutation.isPending;

  function reset() {
    setForm({ email: "", name: "", role: "PRACTICE_MANAGER" });
    setServerError(null);
    setMagicLink(null);
    mutation.reset();
  }

  async function onSubmit() {
    setServerError(null);
    try {
      const res = await mutation.mutateAsync({ ...form, email: form.email.toLowerCase() });
      // Show the placeholder magic link so admins can copy/paste it until
      // the Postmark helper lands (Phase 4.5 — Agent A).
      setMagicLink(res.magicLink);
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | null;
        setServerError(body?.error ?? `Could not invite user (HTTP ${err.status})`);
      } else {
        setServerError("Could not invite user.");
      }
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) reset();
      }}
      title={`Invite user to ${tenantName}`}
      footer={
        magicLink ? (
          <Button
            className="bg-[var(--admin)] hover:bg-[var(--admin-dark)] active:bg-[var(--admin-dark)]"
            onClick={() => {
              onOpenChange(false);
              reset();
            }}
          >
            Done
          </Button>
        ) : (
          <>
            <Button
              variant="secondary"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={onSubmit}
              loading={mutation.isPending}
              disabled={!canSubmit}
              className="bg-[var(--admin)] hover:bg-[var(--admin-dark)] active:bg-[var(--admin-dark)]"
            >
              {mutation.isPending ? "Inviting…" : "Send invitation"}
            </Button>
          </>
        )
      }
    >
      {magicLink ? (
        <div className="grid gap-3">
          <p className="m-0 text-[13px] text-fg-2">
            Invitation created for <strong>{form.email}</strong>. Email delivery wires in Phase 4.5;
            in the meantime, copy this magic link and send it manually.
          </p>
          <pre className="m-0 overflow-x-auto rounded-md bg-bg-1 px-3 py-2 font-mono text-[12px] text-fg-1">
            {magicLink}
          </pre>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigator.clipboard?.writeText(magicLink)}
            className="self-start"
          >
            Copy link
          </Button>
        </div>
      ) : (
        <div className="grid gap-3">
          <Field label="Full name" error={errs.name}>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Dr Anna Lee"
              className="w-full rounded border border-[var(--border-2)] bg-white px-3 py-2 text-[13px] outline-none focus:border-fg-1 focus:ring-2 focus:ring-[var(--border-focus)]"
            />
          </Field>
          <Field label="Email" error={errs.email}>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="anna.lee@northshore.nhs.uk"
              className="w-full rounded border border-[var(--border-2)] bg-white px-3 py-2 text-[13px] outline-none focus:border-fg-1 focus:ring-2 focus:ring-[var(--border-focus)]"
            />
          </Field>
          <Field
            label="Role"
            hint="Studio admins manage every tenant. Client admins manage their own users. Practice managers can order, Approvers sign off proofs, Designers edit drafts, Viewers are read-only."
          >
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value as Role })}
              className="w-full rounded border border-[var(--border-2)] bg-white px-3 py-2 text-[13px] outline-none focus:border-fg-1 focus:ring-2 focus:ring-[var(--border-focus)]"
            >
              {roles.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </Field>
          {serverError && (
            <div className="rounded-md border border-[#E2A0A0] bg-[#FBE9E9] px-3 py-2 text-[13px] text-[#8C2020]">
              {serverError}
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
}

/* ── Tiny form-field wrapper ────────────────────────────────────────── */

function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-[12px] font-semibold text-fg-2">{label}</span>
      {children}
      {hint && !error && <span className="mt-1 block text-[11px] text-fg-3">{hint}</span>}
      {error && <span className="mt-1 block text-[11px] text-[#8C2020]">{error}</span>}
    </label>
  );
}
